// src/components/OrdersNotificationsModal.tsx
import React from 'react';
import { Table, Tag, Space, Button, Empty, Typography, Popconfirm, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import type { Notification as OrderNotif } from '../types/Notification';
import { getStatusMeta } from '../ui/design';
import { formatAmPhoneDisplay, digitsOnly } from '../utils/phone';
import NotifModalShell from './NotifModalShell';
import { useModalTableState } from '../hooks/useModalTableState';
import { formatDateTime } from '../utils/dates';
import { formatCurrency } from '../utils/numbers';

type Props = {
  open: boolean;
  onClose: () => void;
  orders: OrderNotif[];
  onMarkRead: (ids: string[]) => void;
  onDelete: (ids: string[]) => void;
  onOpenOrder?: (id: string) => void;

  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
};

const STATUS_ORDER: Record<string, number> = {
  new: 0,
  processing: 1,
  ready: 2,
  shipped: 3,
  canceled: 4,
  returned: 5,
};

const OrdersNotificationsModal: React.FC<Props> = ({
  open, onClose, orders, onMarkRead, onDelete, onOpenOrder,
  total, page, pageSize, onPageChange,
}) => {
const {
  data,
  selected, setSelected,
  search, setSearch,
  anySelected, anyUnreadSelected,
} = useModalTableState<OrderNotif>(orders, {
  unreadPredicate: (o) => o.isRead === false,
  filterFn: (o, q) => {
    const num = (o.number ?? '').toString().toLowerCase();
    const name = (o.customer ?? '').toLowerCase();
    const phoneDigits = digitsOnly(o.phone ?? '');
    const totalStr = Number.isFinite(o.total as number) ? String(o.total) : '';
    const qDigits = digitsOnly(q);
    return (
      num.includes(q) ||
      name.includes(q) ||
      (!!qDigits && phoneDigits.includes(qDigits)) ||
      totalStr.includes(q)
    );
  },
  debounceMs: 150,
});

  const fmtOrderNo = (raw: unknown): string => {
    const s = String(raw ?? '').trim();
    if (!s) return '—';
    const num = s.match(/\d{4,}/);
    if (num) return `ORD-${num[0]}`;
    const short = s.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-10);
    return `ORD-${short || '0000'}`;
  };

  const columns: ColumnsType<OrderNotif> = [
    {
      title: '№ заказа',
      dataIndex: 'number',
      width: 130,
      render: (v: string) => fmtOrderNo(v),
      sorter: (a, b) => (a.number ?? '').localeCompare(b.number ?? ''),
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      width: 160,
      render: (v?: string) => formatAmPhoneDisplay(v),
      sorter: (a, b) =>
        (a.phone ?? '').replace(/\D/g, '').localeCompare((b.phone ?? '').replace(/\D/g, '')),
    },
    {
      title: 'Сумма',
      dataIndex: 'total',
      width: 120,
      render: (v: number | undefined, r) => formatCurrency(v, r?.currency ?? 'AMD'),
      sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
    },
    {
      title: 'Создан',
      dataIndex: 'createdAt',
      width: 170,
      render: (v?: string) => formatDateTime(v),
      sorter: (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 130,
      render: (v: string) => {
        const m = getStatusMeta(v);
        return <Tag color={m.tag.color} {...(m.tag.style ? { style: m.tag.style } : {})}>{m.chip.label}</Tag>;
      },
      sorter: (a, b) => (STATUS_ORDER[a.status || ''] ?? 999) - (STATUS_ORDER[b.status || ''] ?? 999),
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 230,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => record?.id && onOpenOrder?.(record.id)}>Открыть</Button>
          {!record.isRead && (
            <Button size="small" type="primary" ghost onClick={() => record?.id && onMarkRead([record.id])}>
              Прочитано
            </Button>
          )}
          <Popconfirm
            title="Удалить это уведомление?"
            okText="Да"
            cancelText="Отмена"
            onConfirm={() => record?.id && onDelete([record.id])}
          >
            <Button size="small" danger>Удалить</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];
// ...после объявления columns и до return
const rowSelection = {
  selectedRowKeys: selected,
  onChange: (keys: React.Key[]) => setSelected(keys),
};

return (
  <NotifModalShell
    title="Оповещения — заказы"
    open={open}
    onClose={onClose}
    width={980}
    leftControls={
      <>
        <Button
          disabled={!anyUnreadSelected}
          type="primary"
          ghost
          onClick={() => {
            const ids = data.filter(d => selected.includes(d.id) && !d.isRead).map(d => d.id);
            onMarkRead(ids);
            setSelected([]);
          }}
        >
          Прочитано (выбранные)
        </Button>

        <Button
          disabled={!data.some(d => !d.isRead)}
          onClick={() => {
            const ids = data.filter(d => !d.isRead).map(d => d.id);
            onMarkRead(ids);
            setSelected([]);
          }}
        >
          Прочитано (всё)
        </Button>

        <Input
          allowClear
          placeholder="Поиск: № / телефон / клиент / сумма"
          style={{ width: 320 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
        />
      </>
    }
    rightControls={
      <Popconfirm
        title="Удалить выбранные уведомления?"
        okText="Да"
        cancelText="Отмена"
        onConfirm={() => {
          onDelete(selected as string[]);
          setSelected([]);
        }}
      >
        <Button disabled={!anySelected} danger>Удалить выбранные</Button>
      </Popconfirm>
    }
    hint={
      <Typography.Paragraph type="secondary">
        Подсказка: двойной клик по строке — открыть заказ.
      </Typography.Paragraph>
    }
  >
    {data.length === 0 ? (
      <Empty description="Новых заказов нет" />
    ) : (
      <Table<OrderNotif>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data}
        pagination={{
          ...(page != null ? { current: page } : {}),
          ...(pageSize != null ? { pageSize } : {}),
          total: total ?? 0,
          showSizeChanger: false,
          ...(onPageChange ? { onChange: onPageChange } : {}),
        }}
        rowSelection={rowSelection}
        onRow={(record) => ({ onDoubleClick: () => record?.id && onOpenOrder?.(record.id) })}
        rowClassName={(r) => (r.isRead ? '' : 'ant-table-row-selected')}
        // Колонок больше, чем влезает в телефон: даём таблице собственную
        // горизонтальную прокрутку, иначе она распирала модалку за край экрана.
        // У таблицы сообщений это уже было.
        scroll={{ x: 'max-content' }}
      />
    )}
  </NotifModalShell>
  );
};
export default OrdersNotificationsModal;